import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { supabase } from '../../supabaseClient';
import { AddTask, AddNote, AddList, AddGrocery } from '../../TaskConfig';
import MicButton from './MicButton';
import { useTimezone } from "../../Context/TimezoneContext";
import { toUtcIsoFromLocalInput } from "../../utils/taskDateTime";
import '../../assets/css/checklist.css';

function AddTaskPopUp({actionTitle, show, handleClose, handleSave, categoriesData, fetchSubcategories, subcategoriesData, UserId, onTaskCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(''); // Add state for due date
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]); 
  const [selectedCategory, setSelectedCategory] = useState('0'); // Default to General
  const [selectedSubcategory, setSelectedSubcategory] = useState('null'); // Set default value to "null"
  const [newCategory, setNewCategory] = useState('');
  const [newSubcategory, setNewSubcategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false); // Default to false (General selected)
  const [showNewSubcategoryInput, setShowNewSubcategoryInput] = useState(false); // Set default to false
  const [errorMessage, setErrorMessage] = useState(''); // Add state for error message
  const { userTimezone } = useTimezone();

  useEffect(() => {
    setCategories(categoriesData);
    setSubcategories(subcategoriesData || []);
  }, [categoriesData, subcategoriesData]); 

  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    console.log("[AddTaskPopup] Category changed to:", categoryId);

    if (categoryId === 'new') {
      setShowNewCategoryInput(true);
      setSelectedCategory('');
      setSubcategories([]);
      setSelectedSubcategory('null');
      setShowNewSubcategoryInput(false);
      setNewCategory(''); // Reset new category input
    } else {
      setShowNewCategoryInput(false);
      setSelectedCategory(categoryId);
      setNewCategory(''); // Clear any previous new category text

      if (categoryId && categoryId !== '0') {
        const fetchedSubcategories = await fetchSubcategories(categoryId);
        setSubcategories(fetchedSubcategories || []);
      } else {
        setSubcategories([]);
      }
      setSelectedSubcategory('null');
      setShowNewSubcategoryInput(false);
    }
    setErrorMessage(''); // Clear any previous error
  };

  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    if (subcategoryId === 'new') {
      setShowNewSubcategoryInput(true);
      setSelectedSubcategory('new');
    } else if (subcategoryId === 'null') {
      setShowNewSubcategoryInput(false);
      setSelectedSubcategory(null);
    } else {
      setShowNewSubcategoryInput(false);
      setSelectedSubcategory(subcategoryId);
    }
  };

  const validateDueDate = () => {
    if (!dueDate) {
      setErrorMessage('');
      return true;
    }

    const normalizedDueDate = toUtcIsoFromLocalInput(dueDate, userTimezone);
    if (!normalizedDueDate) {
      setErrorMessage('Invalid due date and time.');
      return false;
    }

    if (new Date(normalizedDueDate).getTime() < Date.now()) {
      setErrorMessage('Due date and time cannot be in the past.');
      return false;
    }
    setErrorMessage('')
    return true;
  };

  const handleSaveClick = async () => {
    if (!title.trim()) {
      setErrorMessage('Title is required.');
      return;
    }
      // if (!dueDate) {
      //   setErrorMessage('Due date is required.');
      //   return;
      // }
    // Bug 12 fix: Check if creating new category (showNewCategoryInput) and newCategory is empty
    // OR if not creating new category and no category is selected (General '0' is valid)
    if (showNewCategoryInput && !newCategory.trim()) {
      setErrorMessage('Category is required.');
      return;
    }
    if (!showNewCategoryInput && !selectedCategory && selectedCategory !== '0') {
      setErrorMessage('Category is required.');
      return;
    }

    if (actionTitle === "Add Task" && dueDate && !validateDueDate()) {
      return;
    }

    setErrorMessage('');
    // Bug 12 fix: Use showNewCategoryInput to determine if creating new category
    const CategoyValue = showNewCategoryInput ? newCategory : selectedCategory;
    const subcategoryValue = selectedSubcategory === null ? null : (selectedSubcategory === 'new' ? newSubcategory : selectedSubcategory);
    const taskData = {
      title,  
      description,
      dueDate,
      category: CategoyValue,
      subcategory: subcategoryValue,
      UserId
    };
    if (actionTitle === "Add Task") {
      await AddTask(taskData);
    } else if (actionTitle === "Add Note") {
      await AddNote(taskData);
    } else if (actionTitle === "Add List") {
      await AddList(taskData);
    } else if (actionTitle === "Add Grocery Item") {
      await AddGrocery(taskData);
    }   
    
    handleClose();
    resetForm(); 
    onTaskCreated(); // Call the callback function to update categories and subcategories
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDueDate('');
    setSelectedCategory('0'); // Reset to General
    setSelectedSubcategory('null'); // Reset to "null"
    setNewCategory('');
    setNewSubcategory('');
    setShowNewCategoryInput(false); // Default to false (General selected)
    setShowNewSubcategoryInput(false); // Set to false
    setErrorMessage('');
  };

  // Bug 12 fix: Use showNewCategoryInput to determine validation (General '0' is valid)
  const isSaveDisabled = !title.trim() || (showNewCategoryInput ? !newCategory.trim() : (!selectedCategory && selectedCategory !== '0')) || !!errorMessage;

  let labelTitle = "Title";

if (actionTitle === "Add Task") {
  labelTitle = "Task";
} else if (actionTitle === "Add Note") {
  labelTitle = "Note";
} else if (actionTitle === "Add List") {
  labelTitle = "List";
} else if (actionTitle === "Add Grocery Item") {
  labelTitle = "Grocery Item";
} else {
  labelTitle = "Title";
}

  // Build category options for React-Select
  const categoryOptions = [
    { value: 'new', label: '+ Create New Category' },
    { value: '0', label: 'General' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name
    }))
  ];

  // Build subcategory options for React-Select
  const subcategoryOptions = [
    { value: 'null', label: 'No Subcategory' },
    { value: 'new', label: '+ Create New Subcategory' },
    ...subcategories.map(subcategory => ({
      value: subcategory.id,
      label: subcategory.name
    }))
  ];

  // Get current selected category value for React-Select
  const getCurrentCategoryValue = () => {
    if (showNewCategoryInput) return { value: 'new', label: '+ Create New Category' };
    if (selectedCategory === '0') return { value: '0', label: 'General' };
    // Use string comparison to handle number/string type differences
    const cat = categories.find(c => String(c.id) === String(selectedCategory));
    return cat ? { value: cat.id, label: cat.name } : { value: 'new', label: '+ Create New Category' };
  };

  // Get current selected subcategory value for React-Select
  const getCurrentSubcategoryValue = () => {
    if (selectedSubcategory === 'null' || selectedSubcategory === null) return { value: 'null', label: 'No Subcategory' };
    if (selectedSubcategory === 'new') return { value: 'new', label: '+ Create New Subcategory' };
    // Use string comparison to handle number/string type differences
    const subcat = subcategories.find(s => String(s.id) === String(selectedSubcategory));
    return subcat ? { value: subcat.id, label: subcat.name } : { value: 'null', label: 'No Subcategory' };
  };

  return (
    <Modal size='lg' show={show} onHide={() => { handleClose(); resetForm(); }} className="task-popup" centered>
      <Modal.Header closeButton>
        <Modal.Title>{actionTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Title Input */}
          <Form.Group controlId="formTitle" className="mb-3">
            <Form.Label>{labelTitle}</Form.Label>
            <Form.Control
              type="text"
              placeholder={`Enter ${labelTitle.toLowerCase()} title`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Group>

          {/* Category & Subcategory Row */}
          {/* <div className="category-row"> */}
            <Form.Group controlId="formCategory" className="mb-3">
              <Form.Label>Category</Form.Label>
              <Select
                className="popup-select-container"
                classNamePrefix="popup-select"
                options={categoryOptions}
                value={getCurrentCategoryValue()}
                onChange={(option) => handleCategoryChange({ target: { value: option?.value || 'new' } })}
                isSearchable={false}
                placeholder="Select Category"
              />
              {showNewCategoryInput && (
                <Form.Control
                  type="text"
                  placeholder="Enter new category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="mt-3"
                />
              )}
            </Form.Group>

            
          {/* </div> */}
          {selectedCategory !== '0' && (
              <Form.Group controlId="formSubcategory" className="mb-3">
                <Form.Label>Subcategory</Form.Label>
                <Select
                  className="popup-select-container"
                  classNamePrefix="popup-select"
                  options={subcategoryOptions}
                  value={getCurrentSubcategoryValue()}
                  onChange={(option) => handleSubcategoryChange({ target: { value: option?.value || 'null' } })}
                  isSearchable={false}
                  placeholder="Select Subcategory"
                />
                {showNewSubcategoryInput && selectedSubcategory !== null && (
                  <Form.Control
                    type="text"
                    placeholder="Enter new subcategory name"
                    value={newSubcategory}
                    onChange={(e) => setNewSubcategory(e.target.value)}
                    className="mt-3"
                  />
                )}
              </Form.Group>
            )}

          {/* Description */}
          <Form.Group controlId="formDescription" className="mb-3">
            <Form.Label>Details (optional)</Form.Label>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter description or details"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ flex: 1 }}
              />
              <MicButton
                onTranscript={(text) => setDescription(prev => prev ? `${prev} ${text}` : text)}
                size="md"
              />
            </div>
          </Form.Group>

          {/* Due Date - Only for Tasks */}
          {actionTitle === "Add Task" && (
            <Form.Group controlId="formDueDate" className="mb-3">
              <Form.Label>Due Date</Form.Label>
              <div className="date-input-wrapper">
                <Form.Control
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <i className="bi bi-calendar date-icon"></i>
              </div>
            </Form.Group>
          )}

          {errorMessage && (
            <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn-popup-cancel" onClick={() => { handleClose(); resetForm(); }}>
          Cancel
        </Button>
        <Button className="btn-popup-save" onClick={handleSaveClick} disabled={isSaveDisabled}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddTaskPopUp;
